/**
 * answer-edit-detection.js
 */

import React from 'react';
import {
  useMediaQuery,
  Grid,
} from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { parseISO, formatISO9075, isValid, addYears } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { EditorIconSection } from 'components/icon-section/icon-section';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import DateTimePicker from 'components/form-inputs/date-time-picker';


export default function AnswerEditDetection(props) {
  const {
    storedRecord,
    drawerState,
    translation,
    workMode,
  } = props;
  const { t } = useTranslation(translation);
  const { control } = useFormContext();
  const upLg = useMediaQuery(theme => theme.breakpoints.up("lg"), { noSsr: true });
  const wasPerformedValue = useWatch({ control, name: 'wasExercisePerformed' });


  return (
    <>
      {wasPerformedValue && workMode === Types.WorkMode.DETECTION &&
      <>
        <EditorIconSection
          iconName='date'
          iconMarginTop={20}
          helpProps={{
            translation,
            section: 'detectionTime'
          }}
        >

          {/* odor time settings */}
          <HelpTextItem
            sidebarText={drawerState.sidebarText}
            title={t('detectionTime.general.helpTitle')}
            text={t('detectionTime.general.helpInfo')}
          >
            {focusHandlers =>
              <EditorAccordion
                highlight
                name="Time Settings"
                summary={<FormSectionTitle>OPTIONAL: Time Settings</FormSectionTitle>}
                detail={
                  <>
                    <Grid 
                      container 
                      spacing={2} 
                      style={{ paddingBottom: 4 }}
                    >
                      {/* odor placement time */}
                      <FormGridItem xs={12} sm={upLg ? 4 : 12}>
                        <DateTimePicker
                          variant='datetime'
                          name='detection.odorPlacementTime'
                          label='Odor Placement Time'
                          focusHandlers={focusHandlers}
                          defaultValue={storedRecord.detection.odorPlacementTime || parseISO(storedRecord.eventDate)}
                          clearable
                          format='MM/dd/yyyy HH:mm'
                          minDate={addYears(parseISO(storedRecord.eventDate), -1)}
                          setValueAs={date => {
                            if (date && isValid(date)) {
                              return formatISO9075(date);
                            }
                            return date;
                          }} 
                        />
                      </FormGridItem>
                      
                      {/* detection start time */}
                      <FormGridItem xs={12} sm={upLg ? 4 : 6}>
                        <DateTimePicker
                          variant='time'
                          name='detection.startTime'
                          label='Detection Start Time'
                          focusHandlers={focusHandlers}
                          defaultValue={storedRecord.detection.detectionStartTime || parseISO(storedRecord.eventDate)}
                          clearable
                          format='HH:mm'
                          setValueAs={date => {
                            if (date && isValid(date)) {
                              const eventDate = parseISO(storedRecord.eventDate);
                              return formatISO9075(new Date(
                                eventDate.getFullYear(), 
                                eventDate.getMonth(), 
                                eventDate.getDate(), 
                                date.getHours(), 
                                date.getMinutes()
                              ));
                            }
                            return date;
                          }} 
                        />
                      </FormGridItem>

                      {/* detection end time */}
                      <FormGridItem xs={12} sm={upLg ? 4 : 6}>
                        <DateTimePicker
                          variant='time'
                          name='detection.endTime'
                          label='Detection End Time'
                          focusHandlers={focusHandlers}
                          defaultValue={storedRecord.detection.detectionEndTime || parseISO(storedRecord.eventDate)}
                          clearable
                          format='HH:mm'
                          setValueAs={date => {
                            if (date && isValid(date)) {
                              const eventDate = parseISO(storedRecord.eventDate);
                              const startTime = control._formValues.detection?.startTime;
                              const startDate = startTime ? parseISO(startTime) : eventDate;
                              
                              // If end time is before start time, add a day
                              let endDate = new Date(
                                eventDate.getFullYear(),
                                eventDate.getMonth(),
                                eventDate.getDate(),
                                date.getHours(),
                                date.getMinutes()
                              );
                              
                              if (startTime && endDate < startDate) {
                                endDate.setDate(endDate.getDate() + 1);
                              }
                              
                              return formatISO9075(endDate);
                            }
                            return date;
                          }} 
                        />
                      </FormGridItem>
                    </Grid>
                  </>
                }
              />}
          </HelpTextItem>
        </EditorIconSection>
      </>}
    </>
  );
}